/* Feb-2026 · Word-Credit Buy Dialog.
 *
 * User-facing dialog for topping up PAYG word credits. Lets the user
 * pick a model (from `/api/word-credits/rates`) + an increment count
 * (1-50 × 10K words), previews the exact USD total, and creates a
 * Stripe Checkout session. On success we redirect to Stripe's hosted
 * checkout URL — the existing webhook credits the balance on
 * `checkout.session.completed`.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Coins, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { fmtUsd } from '@/lib/cost';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

async function api(path, opts = {}) {
  const r = await fetch(`${API_BASE}/api${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...opts,
  });
  if (!r.ok) throw new Error(`${r.status} ${await r.text()}`);
  return r.json();
}

export default function WordCreditBuyDialog({ open, onOpenChange }) {
  const [rates, setRates] = useState(null);
  const [modelId, setModelId] = useState('claude-opus-4-7');
  const [increments, setIncrements] = useState(5);      // 5 × 10K = 50K
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    api('/word-credits/rates').then((r) => { if (!cancelled) setRates(r); });
    return () => { cancelled = true; };
  }, [open]);

  const activeRate = useMemo(() => {
    if (!rates) return null;
    return rates.rates.find((r) => r.model_id === modelId) || rates.rates[0];
  }, [rates, modelId]);

  const total   = activeRate ? activeRate.rate_per_10k_usd * increments : 0;
  const words   = increments * (rates?.increment_words || 10_000);
  const stripeCap = total > 100;

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api('/word-credits/checkout', {
        method: 'POST',
        body:   JSON.stringify({
          model_id:    modelId,
          increments,
          origin_url:  window.location.origin,
        }),
      });
      if (r?.url) window.location.href = r.url;
      else toast.error('Checkout URL missing from response');
    } catch (e) {
      toast.error(`Checkout failed: ${String(e).slice(0, 140)}`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="word-credit-buy-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[14px]">
            <Coins className="h-4 w-4 text-primary" /> Buy Word Credits
          </DialogTitle>
          <DialogDescription className="text-[11.5px]">
            Top up PAYG word credits for premium models (Opus 4.7, GPT-5.4).
            Credits are added to your account the moment Stripe confirms payment.
          </DialogDescription>
        </DialogHeader>

        {!rates ? (
          <div className="py-6 text-center text-[11px] text-muted-foreground">
            <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
            Loading rate card…
          </div>
        ) : (
          <div className="space-y-3 pt-1">
            {/* Model picker */}
            <div>
              <label className="mb-1 block text-[11px] font-semibold">Model</label>
              <div className="grid grid-cols-1 gap-1.5 sm:grid-cols-2">
                {rates.rates.filter((r) => r.premium_metered).map((r) => (
                  <button
                    key={r.model_id}
                    type="button"
                    onClick={() => setModelId(r.model_id)}
                    data-testid={`buy-credit-model-${r.model_id}`}
                    className={`rounded border px-2 py-1.5 text-left text-[11.5px] ${
                      modelId === r.model_id
                        ? 'border-primary bg-primary/10'
                        : 'border-border hover:bg-secondary'}`}
                  >
                    <div className="font-medium">{r.label}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {fmtUsd(r.rate_per_10k_usd)}/10K · {r.provider}
                    </div>
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[10px] text-muted-foreground">
                Standard models (GPT-5.2, Sonnet 4.5, Gemini Flash / Pro) are
                already included with your Studio subscription — no credits needed.
              </p>
            </div>

            {/* Increment slider */}
            <div>
              <label className="mb-1 flex items-center justify-between text-[11px] font-semibold">
                <span>Word credits</span>
                <span className="font-mono-stat tabular-nums text-muted-foreground">
                  {increments} × 10K = {words.toLocaleString()} words
                </span>
              </label>
              <input
                type="range" min="1" max={rates.max_increments || 50} step="1"
                value={increments}
                onChange={(e) => setIncrements(Number(e.target.value))}
                data-testid="buy-credit-increments-slider"
                className="w-full"
              />
              <div className="mt-0.5 flex justify-between text-[9.5px] text-muted-foreground">
                <span>10K</span>
                <span>250K</span>
                <span>500K</span>
              </div>
              {/* Feb-2026 · Bulk-buy bonus tiers (flag: word_credit_bonus_tiers). */}
              {rates.bonus_enabled && Array.isArray(rates.bonus_tiers) && rates.bonus_tiers.length > 0 && (() => {
                const hit = rates.bonus_tiers.find((t) => increments >= t.min_increments);
                const nextTier = [...rates.bonus_tiers].reverse().find((t) => increments < t.min_increments);
                return (
                  <div className="mt-1.5 rounded-sm border border-emerald-500/30 bg-emerald-500/5 px-2 py-1 text-[10.5px]"
                       data-testid="buy-credit-bonus-tier">
                    {hit
                      ? <span className="font-semibold text-emerald-700">Bonus active: +{hit.bonus_pct}% words applied at checkout.</span>
                      : nextTier
                        ? <span className="text-muted-foreground">Add {nextTier.min_increments - increments} more increments to unlock +{nextTier.bonus_pct}% bonus words.</span>
                        : null}
                  </div>
                );
              })()}
            </div>

            {/* Total */}
            <div className="rounded-md border border-primary/40 bg-primary/5 p-2 text-[12px]">
              <div className="flex items-center justify-between">
                <span className="font-semibold">Total (USD, Stripe fee absorbed)</span>
                <span className="font-mono-stat text-primary tabular-nums text-[15px] font-semibold">
                  {fmtUsd(total)}
                </span>
              </div>
              {stripeCap && (
                <div className="mt-1 text-[10px] text-red-700 dark:text-red-300"
                     data-testid="buy-credit-cap-warning">
                  Single top-up exceeds $100 ceiling. Reduce increments or split across purchases.
                </div>
              )}
            </div>

            <div className="rounded bg-amber-50 p-2 text-[10.5px] text-amber-900 dark:bg-amber-950/20 dark:text-amber-200">
              Credits never expire and can be spent across any project. Auto-Recharge
              on OpenAI/Gemini handles the provider side; your credits handle ours.
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button variant="outline" size="sm"
                  onClick={() => onOpenChange?.(false)}
                  data-testid="buy-credit-cancel">Cancel</Button>
          <Button size="sm"
                  disabled={busy || !activeRate || stripeCap}
                  onClick={submit}
                  data-testid="buy-credit-checkout">
            {busy ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <ExternalLink className="mr-1 h-3.5 w-3.5" />}
            Continue to Stripe
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
